import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from './components/Navbar';
import PrivateRoute from './components/PrivateRoute';
import DashboardLayout from './components/DashboardLayout';

// Pages
import Home from './pages/Home';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import VerifyEmail from './pages/auth/VerifyEmail';
import ForgotPassword from './pages/auth/ForgotPassword';
import OAuthSuccess from './pages/auth/OAuthSuccess';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import ChangePassword from './pages/ChangePassword';
import NOCList from './pages/noc/NOCList';
import NOCCreate from './pages/noc/NOCCreate';
import NOCDetail from './pages/noc/NOCDetail';
import Approvals from './pages/staff/Approvals';
import PaymentVerification from './pages/staff/PaymentVerification';
import TimetablePage from './pages/timetable/TimetablePage';
import TimetableManagePage from './pages/timetable/TimetableManagePage';
import TimetableSubjectsPage from './pages/timetable/TimetableSubjectsPage';
import ExamTimetablesPage from './pages/exam/ExamTimetablesPage';
import ExamResultsPage from './pages/exam/ExamResultsPage';
import EventDetail from './pages/academics/EventDetail';
import ExamSection from './pages/academics/ExamSection';
import InternalTests from './pages/academics/InternalTests';
import Practicals from './pages/academics/Practicals';
import SemesterExams from './pages/academics/SemesterExams';
import Assignments from './pages/academics/Assignments';
import MaterialsList from './pages/materials/MaterialsList';
import MaterialUpload from './pages/materials/MaterialUpload';
import FeesDashboard from './pages/fees/FeesDashboard';
import FeesHistory from './pages/fees/FeesHistory';
import VerifyPayments from './pages/fees/VerifyPayments';
import RecordPayment from './pages/fees/RecordPayment';
import PaymentLogs from './pages/fees/PaymentLogs';
import DocumentVault from './pages/documents/DocumentVault';
import Attendance from './pages/attendance/Attendance';
import MarkAttendance from './pages/attendance/MarkAttendance';
import ExportAttendance from './pages/attendance/ExportAttendance';
import Grades from './pages/grades/Grades';
import UpdateGrades from './pages/grades/UpdateGrades';
import Library from './pages/library/Library';
import StudentManagementGateway from './pages/staff/StudentManagementGateway';
import SuperAdminNOC from './pages/staff/SuperAdminNOC';
import SuperAdminStudentManagement from './pages/admin/SuperAdminStudentManagement';
import Reports from './pages/reports/Reports';
import StaffManagement from './pages/admin/StaffManagement';
import StaffCreate from './pages/admin/StaffCreate';
import StaffEdit from './pages/admin/StaffEdit';
import DepartmentManagement from './pages/admin/DepartmentManagement';
import BatchSemesterManagement from './pages/admin/BatchSemesterManagement';

// Coming Soon Pages
import ComingSoon from './pages/ComingSoon';
import Unauthorized from './pages/Unauthorized';
import ConnectionTest from './pages/ConnectionTest';
import Help from './pages/Help';
import Contact from './pages/Contact';
import NotificationsManagement from './pages/staff/NotificationsManagement';
import { useAuth } from './context/AuthContext';

function App() {
  const { isAuthenticated } = useAuth();
  const isProduction = process.env.REACT_APP_MODE === 'production';
  const location = useLocation();
  const publicRoutes = [
    '/',
    '/home',
    '/login',
    '/register',
    '/verify-email',
    '/forgot-password',
    '/auth/oauth-success',
    '/unauthorized',
    '/connection-test',
  ];
  const isPortalRoute = !publicRoutes.includes(location.pathname);

  useEffect(() => {
    if (!isPortalRoute) return;
    window.scrollTo({ top: 0, left: 0 });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [isPortalRoute, location.pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      {!isPortalRoute && <Navbar />}
      <main className={isPortalRoute ? '' : 'flex-1'}>
        <Routes>
          {/* Public Routes */}
          <Route path="/home" element={<Home />} />
          <Route path="/" element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/oauth-success" element={<OAuthSuccess />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/connection-test" element={<ConnectionTest />} />

          {/* Portal Layout (Sidebar + Header) */}
          <Route
            element={
              <PrivateRoute>
                <DashboardLayout />
              </PrivateRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/help" element={<Help />} />
            <Route path="/contact" element={<Contact />} />

            {/* NOC Routes */}
            <Route
              path="/noc"
              element={
                <PrivateRoute allowedRoles={['STUDENT']}>
                  <NOCList />
                </PrivateRoute>
              }
            />
            <Route
              path="/noc/create"
              element={
                <PrivateRoute allowedRoles={['STUDENT']}>
                  <NOCCreate />
                </PrivateRoute>
              }
            />
            <Route
              path="/noc/:id"
              element={
                <PrivateRoute>
                  <NOCDetail />
                </PrivateRoute>
              }
            />

          {/* NOC Approval Routes - Always Available */}
          <Route
            path="/approvals"
            element={
              <PrivateRoute allowedRoles={['LIBRARY_STAFF', 'HOD', 'ACCOUNTS_SECTION', 'EXAM_SECTION']}>
                <Approvals />
              </PrivateRoute>
            }
          />

          <Route
            path="/staff/notifications"
            element={
              <PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'HOD', 'EXAM_SECTION', 'CEO', 'PRINCIPAL', 'VICE_PRINCIPAL', 'DEAN']}>
                <NotificationsManagement />
              </PrivateRoute>
            }
          />

          {/* Payment Logs */}
          <Route
            path="/fees/logs"
            element={
              <PrivateRoute allowedRoles={['ACCOUNTS_SECTION', 'HOD', 'EXAM_SECTION', 'SUPER_ADMIN', 'LIBRARY_STAFF']}>
                <PaymentLogs />
              </PrivateRoute>
            }
          />

            {/* Academic Routes - Coming Soon in Production */}
            <Route
              path="/exam-section"
              element={isProduction ? <ComingSoon title="Exam Section" /> : <PrivateRoute allowedRoles={['STUDENT']}><ExamSection /></PrivateRoute>}
            />
            <Route
              path="/academics/internal-tests"
              element={isProduction ? <ComingSoon title="Internal Tests" /> : <PrivateRoute allowedRoles={['STUDENT', 'HOD', 'ADMIN', 'FACULTY', 'EXAM_SECTION']}><InternalTests /></PrivateRoute>}
            />
            <Route
              path="/academics/practicals"
              element={isProduction ? <ComingSoon title="Practicals" /> : <PrivateRoute><Practicals /></PrivateRoute>}
            />
            <Route
              path="/academics/semester-exams"
              element={isProduction ? <ComingSoon title="Semester Exams" /> : <PrivateRoute allowedRoles={['STUDENT', 'HOD', 'ADMIN', 'FACULTY', 'EXAM_SECTION']}><SemesterExams /></PrivateRoute>}
            />

            {/* Attendance Routes */}
            <Route
              path="/attendance"
              element={<PrivateRoute allowedRoles={['STUDENT']}><Attendance /></PrivateRoute>}
            />

            <Route 
              path="/attendance/mark" 
              element={<PrivateRoute allowedRoles={['FACULTY', 'HOD', 'ADMIN']}><MarkAttendance /></PrivateRoute>} 
            />

            <Route 
              path="/attendance/export" 
              element={<PrivateRoute allowedRoles={['FACULTY', 'HOD', 'ADMIN']}><ExportAttendance /></PrivateRoute>} 
            />

            {/* Grades Routes - Coming Soon in Production */}
            <Route
              path="/grades"
              element={isProduction ? <ComingSoon title="Grades" /> : <PrivateRoute allowedRoles={['STUDENT']}><Grades /></PrivateRoute>}
            />

            {/* Timetable Routes - Coming Soon in Production */}
            <Route
              path="/timetable"
              element={isProduction ? <ComingSoon title="Timetable" /> : <TimetablePage />}
            />

            {/* Materials Routes - Coming Soon in Production */}
            <Route
              path="/materials"
              element={isProduction ? <ComingSoon title="Study Materials" /> : <PrivateRoute><MaterialsList /></PrivateRoute>}
            />

            {/* Fee Management Routes */}
            <Route
              path="/fees"
              element={<PrivateRoute><FeesDashboard /></PrivateRoute>}
            />

            {/* Document Vault - Coming Soon in Production */}
            <Route
              path="/documents"
              element={isProduction ? <ComingSoon title="Document Vault" /> : <PrivateRoute><DocumentVault /></PrivateRoute>}
            />

            {/* Library Route - Coming Soon in Production */}
            <Route
              path="/library"
              element={isProduction ? <ComingSoon title="Library" /> : <PrivateRoute allowedRoles={['STUDENT']}><Library /></PrivateRoute>}
            />

            <Route
              path="/students"
              element={<PrivateRoute allowedRoles={['HOD', 'ADMIN', 'FACULTY', 'ACCOUNTS_SECTION', 'EXAM_SECTION']}><StudentManagementGateway /></PrivateRoute>}
            />

            <Route
              path="/accounts/students"
              element={<PrivateRoute allowedRoles={['ACCOUNTS_SECTION']}><StudentManagementGateway /></PrivateRoute>}
            />

            <Route
              path="/student-management"
              element={<PrivateRoute allowedRoles={['HOD', 'ADMIN', 'FACULTY', 'ACCOUNTS_SECTION', 'EXAM_SECTION']}><StudentManagementGateway /></PrivateRoute>}
            />

            <Route
              path="/student-managent"
              element={<PrivateRoute allowedRoles={['HOD', 'FACULTY']}><StudentManagementGateway /></PrivateRoute>}
            />

            <Route
              path="/grades/update"
              element={isProduction ? <ComingSoon title="Update Grades" /> : <PrivateRoute allowedRoles={['FACULTY', 'HOD', 'ADMIN', 'EXAM_SECTION']}><UpdateGrades /></PrivateRoute>}
            />

            <Route
              path="/timetable/manage"
              element={isProduction ? <ComingSoon title="Manage Timetable" /> : <PrivateRoute allowedRoles={['HOD', 'ADMIN', 'FACULTY']}><TimetableManagePage /></PrivateRoute>}
            />

            <Route
              path="/timetable/manage/subjects"
              element={isProduction ? <ComingSoon title="Manage Subjects" /> : <PrivateRoute allowedRoles={['HOD', 'ADMIN', 'FACULTY']}><TimetableSubjectsPage /></PrivateRoute>}
            />

            <Route
              path="/timetable/subjects"
              element={isProduction ? <ComingSoon title="Subjects" /> : <PrivateRoute allowedRoles={['HOD', 'ADMIN', 'FACULTY', 'EXAM_SECTION']}><TimetableSubjectsPage /></PrivateRoute>}
            />

            <Route
              path="/exam/timetables"
              element={isProduction ? <ComingSoon title="Exam Timetables" /> : <PrivateRoute allowedRoles={['EXAM_SECTION']}><ExamTimetablesPage /></PrivateRoute>}
            />

            <Route
              path="/exam/results"
              element={isProduction ? <ComingSoon title="Exam Results" /> : <PrivateRoute allowedRoles={['EXAM_SECTION']}><ExamResultsPage /></PrivateRoute>}
            />

            <Route
              path="/exam/results/lab-practical"
              element={isProduction ? <ComingSoon title="Lab Practical Results" /> : <PrivateRoute allowedRoles={['EXAM_SECTION']}><ExamResultsPage /></PrivateRoute>}
            />

            <Route
              path="/exam/internal-tests"
              element={<Navigate to="/exam/timetables" replace />}
            />
          </Route>

          <Route
            element={
              <PrivateRoute>
                <DashboardLayout />
              </PrivateRoute>
            }
          >
          {/* Super Admin NOC Management - Always Available */}
          <Route 
            path="/admin/noc-management" 
            element={
              <PrivateRoute allowedRoles={['SUPER_ADMIN']}>
                <SuperAdminNOC />
              </PrivateRoute>
            } 
          />
          
          {/* Super Admin Student Management - Always Available */}
          <Route 
            path="/admin/student-management" 
            element={
              <PrivateRoute allowedRoles={['SUPER_ADMIN']}>
                <SuperAdminStudentManagement />
              </PrivateRoute>
            } 
          />
          
          {/* Conditional Routes - Only in Dev Mode */}
          <Route 
            path="/payment-verification" 
            element={
              isProduction ? (
                <ComingSoon title="Payment Verification" />
              ) : (
                <PrivateRoute allowedRoles={['ACCOUNTS_SECTION', 'HOD', 'ADMIN']}>
                  <PaymentVerification />
                </PrivateRoute>
              )
            } 
          />

          <Route 
            path="/academics/assignments" 
            element={isProduction ? <ComingSoon title="Assignments" /> : <PrivateRoute><Assignments /></PrivateRoute>} 
          />
          <Route 
            path="/academics/events/:id" 
            element={isProduction ? <ComingSoon title="Event Details" /> : <PrivateRoute><EventDetail /></PrivateRoute>} 
          />

          
          <Route 
            path="/materials/upload" 
            element={isProduction ? <ComingSoon title="Upload Materials" /> : <PrivateRoute allowedRoles={['HOD', 'ADMIN', 'FACULTY']}><MaterialUpload /></PrivateRoute>} 
          />

          <Route 
            path="/fees/history" 
            element={<PrivateRoute><FeesHistory /></PrivateRoute>} 
          />
          <Route 
            path="/fees/verify" 
              element={<PrivateRoute allowedRoles={['ACCOUNTS_SECTION', 'SUPER_ADMIN']}><VerifyPayments /></PrivateRoute>} 
          />
          <Route 
            path="/fees/record" 
              element={<PrivateRoute allowedRoles={['ACCOUNTS_SECTION', 'SUPER_ADMIN']}><RecordPayment /></PrivateRoute>} 
          />
          {/* Student Management - Coming Soon in Production */}
          {/* Reports - Coming Soon in Production */}
          <Route 
            path="/reports" 
            element={isProduction ? <ComingSoon title="Reports" /> : <PrivateRoute allowedRoles={['HOD', 'ADMIN', 'FACULTY']}><Reports /></PrivateRoute>} 
          />

          {/* Admin Routes - Enabled for SUPER_ADMIN */}
          <Route 
            path="/admin/staff" 
            element={<PrivateRoute allowedRoles={['SUPER_ADMIN']}><StaffManagement /></PrivateRoute>} 
          />
          <Route 
            path="/admin/staff/create" 
            element={<PrivateRoute allowedRoles={['SUPER_ADMIN']}><StaffCreate /></PrivateRoute>} 
          />
          <Route 
            path="/admin/staff/:id" 
            element={<PrivateRoute allowedRoles={['SUPER_ADMIN']}><StaffEdit /></PrivateRoute>} 
          />
          <Route 
            path="/admin/departments" 
            element={<PrivateRoute allowedRoles={['SUPER_ADMIN']}><DepartmentManagement /></PrivateRoute>} 
          />
          <Route 
            path="/admin/batch-semesters" 
            element={isProduction ? <ComingSoon title="Batch Semester Management" /> : <PrivateRoute allowedRoles={['SUPER_ADMIN']}><BatchSemesterManagement /></PrivateRoute>} 
          />

          {/* Always Coming Soon */}
          <Route path="/courses" element={<ComingSoon title="Course Management" />} />
          <Route path="/chatbot" element={<ComingSoon title="AI Chatbot" />} />
          <Route path="/events" element={<ComingSoon title="Events & Notices" />} />
          <Route path="/exam" element={<ComingSoon title="Examination Module" />} />
          <Route path="/faculty" element={<ComingSoon title="Faculty Management" />} />
          <Route path="/admin" element={<ComingSoon title="Admin Controls" />} />

          </Route>

          {/* 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

export default App;
